import { useMemo } from "react";
import {
  FEATURE_CONFIG,
  VALIDATION_MESSAGES,
} from "../components/settings/constants";
import { SFDCIntegration, SFDCIntegrationSyncSettings } from "../types";

interface ValidationResult {
  isValid: boolean;
  errors: {
    recordTypes?: string;
    enrichment?: string;
  };
}

/**
 * Hook to validate Salesforce integration settings using FEATURE_CONFIG
 * Centralizes all validation logic and eliminates hardcoded version checks
 */
export const useSettingsValidation = (
  integration: SFDCIntegration | undefined,
  settings: SFDCIntegrationSyncSettings | undefined
): ValidationResult => {
  return useMemo(() => {
    if (!integration || !settings) {
      return {
        isValid: false,
        errors: {
          recordTypes: VALIDATION_MESSAGES.noIntegrationOrSettings,
        },
      };
    }

    const version = integration.managedPackageCurrentVersion;
    const errors: ValidationResult["errors"] = {};

    // Validate Record Types section
    const hasRecordType = FEATURE_CONFIG.recordTypes.fields.some(
      (field) => settings[field.name as keyof SFDCIntegrationSyncSettings]
    );

    if (!hasRecordType) {
      errors.recordTypes = VALIDATION_MESSAGES.recordTypesRequired;
    }

    // Validate HG Data Enrichment section
    // Only check features that are available based on version
    const availableEnrichmentFields =
      FEATURE_CONFIG.hgDataEnrichment.fields.filter((field) => {
        // Check if the feature is available based on version
        if (field.versionCheck) {
          return field.versionCheck(version);
        }
        return false;
      });

    // Check if at least one available enrichment option is selected
    const hasEnrichment = availableEnrichmentFields.some(
      (field) => settings[field.name as keyof SFDCIntegrationSyncSettings]
    );

    if (!hasEnrichment) {
      if (availableEnrichmentFields.length === 0) {
        errors.enrichment = VALIDATION_MESSAGES.noEnrichmentFeaturesAvailable;
      } else {
        errors.enrichment = VALIDATION_MESSAGES.enrichmentRequired;
      }
    }

    return {
      isValid: !errors.recordTypes && !errors.enrichment,
      errors,
    };
  }, [integration, settings]);
};
